import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE, euro } from "../lib/api";
import { Download, TrendingUp, Wallet, Coins, Info, Receipt, PiggyBank } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Finances() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finances/summary");
      setData(data);
    } catch (e) {
      console.error("Failed to load finances summary", e);
    }
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => load();
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const togglePfl = async () => {
    await api.put("/auth/profile", { pfl_option: !user?.pfl_option });
    await refreshUser();
    load();
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("mcb_token");
    const res = await fetch(`${API_BASE}/finances/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `export_comptable_${new Date().getFullYear()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="text-muted-soft">Chargement…</div>;

  const maxRevenue = Math.max(...data.months.map((m) => m.revenue), 1);

  return (
    <div className="space-y-8" data-testid="finances-container">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Mes finances</h1>
          <p className="text-muted-soft mt-1">Une vision claire, sans jargon comptable.</p>
        </div>
        <button onClick={exportCsv} className="btn-ghost text-sm" data-testid="export-btn">
          <Download className="inline w-4 h-4 mr-1.5" strokeWidth={1.6} />
          Export comptable (CSV)
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="card-soft p-7" data-testid="yearly-revenue-card">
          <div className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center mb-3"><TrendingUp className="w-5 h-5 text-sage" strokeWidth={1.5} /></div>
          <div className="text-sm text-muted-soft mb-1">Encaissé en {data.year}</div>
          <div className="font-display text-3xl">{euro(data.yearly_revenue)}</div>
        </div>
        <div className="card-soft p-7" data-testid="urssaf-yearly-card">
          <div className="w-10 h-10 rounded-xl bg-beige flex items-center justify-center mb-3"><Wallet className="w-5 h-5" style={{ color: "#8B6F4D" }} strokeWidth={1.5} /></div>
          <div className="text-sm text-muted-soft mb-1">Charges sociales URSSAF</div>
          <div className="font-display text-3xl">- {euro(data.yearly_urssaf)}</div>
          <div className="text-xs text-muted-soft mt-1">Régime : {data.urssaf_regime_label} · taux {(data.urssaf_rate * 100).toFixed(1).replace(".", ",")} %{data.pfl_option ? " (avec PFL)" : ""}</div>
        </div>
        <div className="card-soft p-7" data-testid="expenses-yearly-card">
          <div className="w-10 h-10 rounded-xl bg-beige flex items-center justify-center mb-3"><Receipt className="w-5 h-5" style={{ color: "#8B6F4D" }} strokeWidth={1.5} /></div>
          <div className="text-sm text-muted-soft mb-1">Frais fournisseurs payés</div>
          <div className="font-display text-3xl">- {euro(data.yearly_expenses || 0)}</div>
          <div className="text-xs text-muted-soft mt-1">Loyer, matériel, formations, assurance…</div>
        </div>
        <div className="card-soft p-7" style={{ backgroundColor: "var(--sage-light)" }} data-testid="net-real-card">
          <div className="w-10 h-10 rounded-xl bg-sage flex items-center justify-center mb-3"><PiggyBank className="w-5 h-5 text-white" strokeWidth={1.5} /></div>
          <div className="text-sm text-muted-soft mb-1">Net réel (ce qu'il vous reste)</div>
          <div className="font-display text-3xl text-sage">{euro(data.yearly_net_reel ?? data.yearly_after_charges)}</div>
          <div className="text-xs text-muted-soft mt-1">Après charges sociales et factures fournisseurs</div>
        </div>
      </div>

      <div className="card-soft p-7" data-testid="net-after-card-secondary">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center flex-shrink-0">
            <Coins className="w-5 h-5 text-sage" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="text-sm text-muted-soft">Reste après URSSAF (avant frais fournisseurs)</div>
            <div className="font-display text-xl">{euro(data.yearly_after_charges)}</div>
          </div>
          <div className="text-xs text-muted-soft text-right max-w-[280px]">
            En micro-BNC, vos charges ne sont pas déductibles fiscalement. Mais elles le sont pour votre tréso : c'est le « Net réel ».
          </div>
        </div>
      </div>

      <div className="card-soft p-7" data-testid="tva-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-lg">Seuil de TVA · franchise en base</div>
            <div className="text-xs text-muted-soft mt-1">37 500 € de chiffre d'affaires annuel · au-delà, vous devrez facturer la TVA.</div>
          </div>
          <div className="text-sm font-medium text-sage">{data.tva_progress}%</div>
        </div>
        <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: "var(--sage-light)" }}>
          <div className="h-full rounded-full bg-sage transition-all" style={{ width: `${Math.min(100, data.tva_progress)}%` }} />
        </div>
      </div>

      <div className="card-soft p-7" data-testid="months-chart-card">
        <div className="font-display text-lg mb-4">Mois par mois ({data.year})</div>
        <div className="grid grid-cols-12 gap-2 items-end h-48">
          {data.months.map((m) => (
            <div key={m.month} className="flex flex-col items-center gap-2 group">
              <div className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">{euro(m.revenue)}</div>
              <div className="w-full rounded-t-lg bg-sage hover:opacity-80 transition-all" style={{ height: `${(m.revenue / maxRevenue) * 130}px`, minHeight: 2 }} />
              <div className="text-xs text-muted-soft">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-soft p-7" data-testid="quarters-card">
        <div className="font-display text-lg mb-4">Trimestres URSSAF</div>
        <div className="grid sm:grid-cols-4 gap-3">
          {data.quarters.map((q) => (
            <div key={q.quarter} className="rounded-xl p-4 border" style={{ borderColor: "var(--border)" }}>
              <div className="text-sm text-muted-soft">{q.quarter}</div>
              <div className="font-display text-xl mt-1">{euro(q.revenue)}</div>
              <div className="text-xs text-sage mt-1">URSSAF : {euro(q.urssaf)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-soft p-7" data-testid="pfl-card">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-sage" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg mb-1">Option Versement Forfaitaire Libératoire (PFL)</div>
            <p className="text-sm text-muted-soft mb-3 leading-relaxed">
              Si vous l'avez choisie, votre impôt sur le revenu est payé en même temps que les cotisations sociales,
              avec un taux supplémentaire de 2,2 % sur votre chiffre d'affaires (BNC prestations).
            </p>
            <button onClick={togglePfl} className={user?.pfl_option ? "btn-sage text-sm" : "btn-ghost text-sm"} data-testid="toggle-pfl">
              {user?.pfl_option ? "PFL activée — désactiver" : "Activer l'option PFL"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
